import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';

import { inboundAclAdd, inboundAclUpdate } from '../../../store/slices/tenantSlices/incomingSlice';

// Plain IPv4 — the inbound-acl endpoint stores a single host address, not a range.
const IPV4_REGEX = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

const AddEditInboundAclModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const initialValues = {
    name: '',
    ip_address: ''
  };

  const validationSchema = Yup.object({
    name: Yup.string().trim().required('Name is required'),
    ip_address: Yup.string()
      .trim()
      .required('IP address is required')
      .matches(IPV4_REGEX, 'Enter a valid IPv4 address (e.g. 192.168.1.10)')
  });

  const onSubmit = async (values, { resetForm }) => {
    try {
      const body = { name: values.name.trim(), ip_address: values.ip_address.trim() };
      const action = isEdit ? inboundAclUpdate({ id: editRecord.id, ...body }) : inboundAclAdd(body);
      const response = await dispatch(action).unwrap();
      if (response) {
        toast.success(
          response?.message || (isEdit ? 'Record Updated Successfully' : 'Record Added Successfully')
        );
        onCloseClick(true);
        resetForm();
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, handleSubmit, touched, errors, resetForm, setValues } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  useEffect(() => {
    if (isEdit && editRecord) {
      setValues({
        name: editRecord.name || '',
        ip_address: editRecord.ip_address || ''
      });
    } else {
      resetForm();
    }
  }, [isEdit, editRecord, isOpen]);

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Inbound ACL' : 'Add Inbound ACL'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="inbound-acl-form" onSubmit={handleSubmit}>
          <Row className="mt-3">
            <Col lg="6">
              <FormInput
                name="name"
                label="Name"
                placeholder="e.g. Office Network"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="6">
              <FormInput
                name="ip_address"
                label="IP Address"
                placeholder="e.g. 192.168.1.10"
                value={values.ip_address}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="inbound-acl-form" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditInboundAclModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditInboundAclModal;
